import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, FormSection, InfoButton, Inline, Legend, Link, NumberInput, Panel, RestrictedState, SaveBar, Skeleton, Stack, t, Text, toast } from '@kanbios/ui';
import { RefusalPage } from '../../components/Refusal';
import { api, ApiFailure, unwrap } from '../../lib/api';
import { relativeTime } from '../../lib/format';
import { useSession } from '../../lib/session';
import { changeRef } from '../../components/catalogue/PullRequestNotice';

/** Chap. 9 order of the rule: the first criterion met wins (SPEC-008 RG-008-2). */
const ORDER = ['maintenance', 'incident', 'unknown', 'to_update', 'ok'] as const;
const GROUPS = ['unknown', 'to_update', 'incident'] as const;
/** Fields whose hint says more than the range. */
const NOTES = new Set(['diskIncidentPercent', 'cisWarnBelowPercent', 'backupWarnAfterHours', 'osEolWarnMonths']);

/**
 * Réglages › « Seuils et code couleur » (SPEC-013): the values in force, one
 * pull request for a change, read again by the aggregator after the merge
 * (RG-013-7). The fields keep the values in force until then (RG-013-4).
 */
export function ThresholdsSection({ onFooter }: { onFooter: (footer: ReactNode) => void }) {
  const { enums } = useSession();
  const queryClient = useQueryClient();
  const section = useQuery({
    queryKey: ['settings-thresholds'],
    queryFn: async () => unwrap(await api.maintenance.GET('/settings/thresholds')),
    refetchInterval: 15_000,
  });
  const data = section.data;
  const [draft, setDraft] = useState<Record<string, number | null>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const changed = useMemo(() => Object.entries(draft).filter(([field, v]) => data && v !== data.values[field]), [draft, data]);

  const propose = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const r = unwrap(await api.maintenance.PUT('/settings/thresholds', { body: { ...data.values, ...Object.fromEntries(changed) } as Record<string, number> }));
      toast({ tone: 'pos', title: t('thresholds.opened', { ref: changeRef(r.pullRequest) }) });
      setDraft({});
      setErrors({});
      await queryClient.invalidateQueries({ queryKey: ['settings-thresholds'] });
    } catch (e) {
      if (e instanceof ApiFailure) {
        const fields = ((e.body.details as { fields?: { field: string; message: string }[] } | undefined)?.fields ?? []);
        setErrors(Object.fromEntries(fields.map(f => [f.field, f.message])));
        if (!fields.length) toast({ tone: 'neg', title: e.body.message, description: e.body.hint });
      }
    } finally {
      setSaving(false);
    }
  };

  // The save bar lives in the AppShell footer (LayoutSettings); it calls the latest proposal.
  const proposeRef = useRef(propose);
  proposeRef.current = propose;
  useEffect(() => {
    onFooter(data?.canUpdate && changed.length ? (
      <SaveBar
        count={changed.length}
        message={t('thresholds.unsaved', { n: changed.length })}
        saveLabel={t('thresholds.propose')}
        cancelLabel={t('thresholds.cancel')}
        saving={saving}
        onSave={() => void proposeRef.current()}
        onCancel={() => { setDraft({}); setErrors({}); }}
      />
    ) : null);
    return () => onFooter(null);
  }, [data, changed.length, saving, onFooter]);

  if (section.error) return <RefusalPage error={section.error} />;
  const state = (value: string) => enums.states.find(s => s.value === value);
  const pr = data?.pullRequest;
  const live = pr && (pr.state === 'open' || pr.state === 'validation_failed' || pr.state === 'merged');
  return (
    <Panel
      title={t('settings.tabs.thresholds')}
      subtitle={data?.readAt ? t('thresholds.readAt', { when: relativeTime(data.readAt) }) : t('thresholds.defaults')}
      actions={(
        <InfoButton label={t('thresholds.orderLabel')} title={t('thresholds.orderTitle')} size="sm">
          <Stack gap={8}>
            <Text variant="body-sm">{t('thresholds.orderLead')}</Text>
            <Legend display="badge" label={t('thresholds.orderTitle')}
              items={ORDER.map(s => ({ tone: state(s)?.tone ?? 'neutral', label: state(s)?.label ?? s, description: t(`thresholds.rule.${s}`) }))} />
          </Stack>
        </InfoButton>
      )}
    >
      {!data ? <Skeleton lines={6} /> : (
        <Stack gap={16}>
          {!data.canUpdate && <Inline><RestrictedState variant="chip" label={t('thresholds.readOnly')} /></Inline>}
          {live && (
            <Alert tone={pr.state === 'validation_failed' ? 'neg' : 'info'} title={t(`thresholds.pr.${pr.state}`, { ref: changeRef(pr), message: pr.ciMessage ?? '' })}>
              <Link href={pr.url} external>{t('pullRequest.link', { ref: changeRef(pr) })}</Link>
            </Alert>
          )}
          {pr?.state === 'applied' && <Alert tone="pos" title={t('thresholds.pr.applied', { ref: changeRef(pr) })} />}
          {GROUPS.map(group => (
            <FormSection key={group} title={state(group)?.label ?? group} description={t(`thresholds.group.${group}`)} columns={2}>
              {data.fields.filter(f => f.state === group).map(f => (
                <NumberInput key={f.field} block label={t(`thresholds.fields.${f.field}`)} unit={t(`thresholds.units.${f.unit}`)} min={f.min} max={f.max} step={1}
                  value={draft[f.field] !== undefined ? draft[f.field] : data.values[f.field] ?? null}
                  hint={[t('thresholds.range', { min: f.min, max: f.max, unit: t(`thresholds.units.${f.unit}`), default: f.default }), NOTES.has(f.field) ? t(`thresholds.applies.${f.field}`) : ''].filter(Boolean).join(' ')}
                  error={errors[f.field]} disabled={!data.canUpdate || Boolean(live)}
                  onChange={(v: number | null) => setDraft(d => ({ ...d, [f.field]: v }))} />
              ))}
            </FormSection>
          ))}
        </Stack>
      )}
    </Panel>
  );
}
